import classNames from "classnames";
import React, { Dispatch, FC, ReactNode, SetStateAction } from "react";

import { Card } from "../../../components/card/Card";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { ListPager } from "../../../components/pagination/ListPager";
import {
  HomeFilter,
  HomeTab,
} from "../../../factories/works/models/home-enums";
import { WorkModel } from "../../../factories/works/models/work.model";
import { WorkTableHeaderItem, WorkTableSort } from "../types/work-table";
import { WorkTable } from "./WorkTable";

type Props = {
  works: WorkModel[];
  isLoading?: boolean;
  hasPages?: boolean;
  children?: ReactNode;
  myWorkHome?: boolean;
  filter?: HomeTab | HomeFilter.OverDue;
  setArchiveWorks?: Dispatch<SetStateAction<WorkModel[]>>;
  archiveWorks?: WorkModel[];
  statusFilter?: string[];
  page: number;
  totalPages: number;
  /** Total work items and page size, for "Showing 21–40 of 75". */
  total?: number;
  pageSize?: number;
  onPageChange: (nextPage: number) => void;
  sortBy: WorkTableSort;
  sortOrder: "asc" | "desc";
  onSortChange: (sortBy: WorkTableSort, sortOrder: "asc" | "desc") => void;
  showSelection?: boolean;
  headerItems?: WorkTableHeaderItem[];
  /** Shown instead of the table when there are no works (default: "There are no work items"). */
  emptyState?: ReactNode;
  /** Marks this work item's row as the one being viewed. */
  currentWorkId?: string;
};

export const WorkList: FC<Props> = ({
  works,
  isLoading,
  hasPages,
  children,
  myWorkHome,
  filter,
  setArchiveWorks,
  archiveWorks,
  statusFilter,
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  sortBy,
  sortOrder,
  onSortChange,
  showSelection = true,
  headerItems,
  emptyState,
  currentWorkId,
}) => {
  return (
    <Card extraClasses={"grid grid-rows-1"}>
      <div
        className={classNames(
          "grid h-full w-full gap-4",
          hasPages
            ? "grid-rows-[max-content_1fr_max-content]"
            : "grid-rows-[max-content_1fr]",
        )}>
        {children}

        {isLoading ? (
          <div>
            <LottieLoading />
          </div>
        ) : emptyState && !works?.length ? (
          emptyState
        ) : (
          <div className={"max-h-full w-full overflow-x-auto px-6"}>
            <WorkTable
              works={works}
              statusFilter={statusFilter}
              myWorkHome={myWorkHome}
              filter={filter}
              setArchiveWorks={setArchiveWorks}
              archiveWorks={archiveWorks}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={onSortChange}
              showSelection={showSelection}
              headerItems={headerItems}
              currentWorkId={currentWorkId}
            />
          </div>
        )}

        {hasPages && !!works?.length && (
          <ListPager
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            disabled={isLoading}
            emptyLabel={"No work items"}
            onChange={onPageChange}
          />
        )}
      </div>
    </Card>
  );
};
